/**
 * f12.2: keep an open review screen or forecast grid live (adr-3, adr-14).
 *
 * Opens ONE authenticated SSE stream for the page's subscription and writes
 * every pushed projection straight into the TanStack Query cache under the
 * page's OWN key, through `mergeLive` -- so the page re-renders from the cache
 * write alone and there is no second fetching path. Every (re)connect starts
 * with a full snapshot, so a dropped connection resynchronises by itself.
 *
 * `fetchEventSource` rather than `EventSource`, for its reconnect control. The
 * stream rides the session cookie like every other call (f17.3); the server
 * ends it at the session's expiry (or `LIVE_STREAM_MAX_SECONDS`) and we
 * reconnect at once. The library
 * sends `Last-Event-ID` on reconnect, and with `openWhenHidden: false` a hidden
 * tab closes its stream; becoming visible reconnects and resyncs.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { CSRF_HEADER, apiUrl } from '@/api/client';
import type { ForecastGrid, LiveClosedEvent, LiveFiguresEvent, Review } from '@/api/client';
import { returnToSignIn } from '@/auth/signIn';
import { forecastGridQueryKey } from '@/features/forecast/useForecastGrid';
import { reviewQueryKey } from '@/routes/cycles/review/useReview';

import { mergeGrid, mergeReview } from './mergeLive';

export type LiveStatus = 'connecting' | 'live' | 'down' | 'closed';
export type ClosedReason = LiveClosedEvent['reason'];

export type LiveSubscription =
  | { cycleId: string; view: 'review'; unitIds: readonly string[] }
  | { cycleId: string; view: 'grid'; businessUnitId: string };

export interface LiveState {
  status: LiveStatus;
  /** `pushed_at` of the last figures event, as epoch milliseconds. */
  lastPushedAt: number | null;
  closedReason: ClosedReason | null;
}

const RETRY_MS = 3000;
const MAX_RETRY_MS = 30000;

/** Stops the stream for good: not signed in, 401, 404 or `event: closed`. */
class StreamStopped extends Error {}
/** The server ended the stream on purpose: reconnect at once with a fresh token. */
class StreamEnded extends Error {}

function streamPath(subscription: LiveSubscription): string {
  const base = `/cycles/${encodeURIComponent(subscription.cycleId)}/live?view=${subscription.view}`;
  if (subscription.view === 'grid') return `${base}&business_unit_id=${encodeURIComponent(subscription.businessUnitId)}`;
  return `${base}&${[...subscription.unitIds].sort().map((id) => `unit=${encodeURIComponent(id)}`).join('&')}`;
}

function isReady(subscription: LiveSubscription): boolean {
  if (!subscription.cycleId) return false;
  return subscription.view === 'grid' ? Boolean(subscription.businessUnitId) : subscription.unitIds.length > 0;
}

/**
 * `fetch` with the session cookie the browser attaches itself (f17.3) and the
 * CSRF header every API call carries. No credential is handled here.
 */
async function sessionFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set(CSRF_HEADER, 'Atlas');
  return fetch(input, { ...init, headers, cache: 'no-store', credentials: 'same-origin' });
}

export function useLiveCycle(subscription: LiveSubscription): LiveState {
  const client = useQueryClient();
  const [state, setState] = useState<LiveState>({ status: 'connecting', lastPushedAt: null, closedReason: null });
  const path = isReady(subscription) ? streamPath(subscription) : '';
  const subscriptionRef = useRef(subscription);
  subscriptionRef.current = subscription;

  useEffect(() => {
    if (!path) return undefined;
    setState({ status: 'connecting', lastPushedAt: null, closedReason: null });
    const abort = new AbortController();
    const current = subscriptionRef.current;
    let retry = RETRY_MS;

    const applyFigures = (payload: LiveFiguresEvent) => {
      if (current.view === 'review') {
        client.setQueryData<Review>(reviewQueryKey(current.cycleId, current.unitIds), (cached) =>
          mergeReview(cached, payload.data as Review),
        );
      } else {
        client.setQueryData<ForecastGrid>(forecastGridQueryKey(current.cycleId, current.businessUnitId), (cached) =>
          mergeGrid(cached, payload.data as ForecastGrid),
        );
      }
      const pushed = Date.parse(payload.pushed_at);
      setState({ status: 'live', lastPushedAt: Number.isNaN(pushed) ? Date.now() : pushed, closedReason: null });
    };

    const stop = (reason: ClosedReason | null) => {
      if (reason) setState({ status: 'closed', lastPushedAt: null, closedReason: reason });
      throw new StreamStopped(reason ?? 'stopped');
    };

    fetchEventSource(apiUrl(path), {
      signal: abort.signal,
      openWhenHidden: false,
      fetch: sessionFetch,
      async onopen(response) {
        if (response.status === 401) {
          returnToSignIn();
          stop(null);
        }
        if (response.status === 404) stop('ACCESS_REVOKED');
        if (!response.ok) throw new Error(`Live stream answered ${response.status}`);
        retry = RETRY_MS;
      },
      onmessage(event) {
        if (event.event === 'figures') applyFigures(JSON.parse(event.data) as LiveFiguresEvent);
        if (event.event === 'closed') stop((JSON.parse(event.data) as LiveClosedEvent).reason);
      },
      onclose() {
        throw new StreamEnded();
      },
      onerror(error) {
        if (error instanceof StreamStopped) throw error;
        if (error instanceof StreamEnded) return 0;
        setState((prev) => ({ ...prev, status: 'down' }));
        const wait = retry;
        retry = Math.min(retry * 2, MAX_RETRY_MS);
        return wait;
      },
    }).catch(() => undefined);

    return () => abort.abort();
  }, [client, path]);

  return state;
}

/** How long the stream may be down before the pages' 2 s polling takes over. */
export const FALLBACK_AFTER_MS = 5000;

/**
 * Whether the pages should poll: only once the stream has NOT been live for
 * more than `FALLBACK_AFTER_MS`. Off again on the next snapshot.
 */
export function useLiveFallback(status: LiveStatus): boolean {
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    if (status === 'live') {
      setFallback(false);
      return undefined;
    }
    const timer = setTimeout(() => setFallback(true), FALLBACK_AFTER_MS);
    return () => clearTimeout(timer);
  }, [status]);
  return fallback;
}
